import { useClerk, useUser } from "@clerk/clerk-react";
import React, { useEffect, useMemo, useState } from "react";
import {
    FaAward,
    FaEnvelope,
    FaExternalLinkAlt,
    FaGlobe,
    FaHeart,
    FaMapMarkerAlt,
    FaPhone,
    FaRegHeart,
    FaShareAlt,
    FaSms,
    FaStar
} from "react-icons/fa";
import { FaGem } from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import QuoteModal from "../../components/QuoteModal/QuoteModal";
import { API_URL } from "../../utils/constants";
import "./ProviderProfile.css";

const ProviderProfile = () => {
    const { isLoaded, isSignedIn, user } = useUser();
    const { openSignIn } = useClerk();
    const { id } = useParams();
    const navigate = useNavigate();
    const [provider, setProvider] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({
        average_rating: 0,
        total_reviews: 0,
    });
    const [activeTab, setActiveTab] = useState("reviews");
    const [isLiked, setIsLiked] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [loadingProvider, setLoadingProvider] = useState(true);
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);
    const [error, setError] = useState(null);
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [showLinkCopied, setShowLinkCopied] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);

    // Helper function to check if user needs to sign in for a feature
    const requireSignIn = () => {
        if (!isSignedIn) {
            openSignIn({
                redirectUrl: window.location.href,
            });
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (!id) {
            setLoadingProvider(false);
            setError("Provider ID is missing.");
            return;
        }

        const fetchProvider = async () => {
            setLoadingProvider(true);
            setError(null);
            try {
                let fetchUrl;
                let shouldTryPublicFallback = false;
                
                // If Clerk is loaded and user is signed in, use authenticated endpoint
                if (isLoaded && isSignedIn && user) {
                    // Use authenticated endpoint with user credentials
                    const params = new URLSearchParams();
                    params.append('user_id', user.id);
                    if (user.primaryEmailAddress?.emailAddress) {
                        params.append('email', user.primaryEmailAddress.emailAddress);
                    }
                    fetchUrl = `${API_URL}/api/providers/${id}?${params.toString()}`;
                    shouldTryPublicFallback = true;
                } else {
                    // Use public endpoint for anonymous users or if Clerk hasn't loaded yet
                    fetchUrl = `${API_URL}/api/providers/public/${id}`;
                }

                const res = await fetch(fetchUrl);

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    
                    // If authenticated user gets "not accessible" but provider might have public reviews, try public endpoint
                    if (shouldTryPublicFallback && 
                        (errData.message?.includes("not accessible") || errData.message?.includes("not found"))) {
                        // console.log("Authenticated access failed, trying public endpoint as fallback...");
                        
                        const publicRes = await fetch(`${API_URL}/api/providers/public/${id}`);
                        if (publicRes.ok) {
                            const publicData = await publicRes.json();
                            if (publicData.success) {
                                setProvider(publicData.provider);
                                setIsLiked(false); // Public endpoint doesn't include user-specific like status
                                return;
                            }
                        }
                    }
                    
                    throw new Error(
                        errData.message || `HTTP error! status: ${res.status}`
                    );
                }

                const data = await res.json();
                if (data.success) {
                    // console.log('Provider data received:', {
                    //     provider_id: data.provider.id,
                    //     business_name: data.provider.business_name,
                    //     visible_reviews_type: typeof data.provider.visible_reviews,
                    //     visible_reviews_length: Array.isArray(data.provider.visible_reviews) ? data.provider.visible_reviews.length : 'not array',
                    //     users_who_reviewed_type: typeof data.provider.users_who_reviewed,
                    //     users_who_reviewed: data.provider.users_who_reviewed
                    // });
                    setProvider(data.provider);
                    setIsLiked(data.provider.currentUserLiked || false);
                } else {
                    // Try public endpoint fallback for signed-in users
                    if (shouldTryPublicFallback && 
                        (data.message?.includes("not accessible") || data.message?.includes("not found"))) {
                        // console.log("Authenticated request failed, trying public endpoint as fallback...");
                        
                        const publicRes = await fetch(`${API_URL}/api/providers/public/${id}`);
                        if (publicRes.ok) {
                            const publicData = await publicRes.json();
                            if (publicData.success) {
                                setProvider(publicData.provider);
                                setIsLiked(false); // Public endpoint doesn't include user-specific like status
                                return;
                            }
                        }
                    }
                    
                    throw new Error(
                        data.message || "Failed to fetch provider details"
                    );
                }
            } catch (error) {
                console.error("Failed to fetch provider:", error);
                setError(error.message);
                setProvider(null);
            } finally {
                setLoadingProvider(false);
            }
        };

        fetchProvider();
    }, [id, isLoaded, isSignedIn, user]);

    // Extract reviews from provider data instead of separate API call
    useEffect(() => {
        setLoadingReviews(true);
        if (provider) {
            let visibleReviews = [];
            
            // Handle visible_reviews from authenticated endpoint
            if (provider.visible_reviews) {
                try {
                    // Handle both JSON string and array cases
                    if (typeof provider.visible_reviews === 'string') {
                        visibleReviews = JSON.parse(provider.visible_reviews);
                    } else if (Array.isArray(provider.visible_reviews)) {
                        visibleReviews = provider.visible_reviews;
                    }
                } catch (e) {
                    console.error('Error parsing visible_reviews:', e);
                    visibleReviews = [];
                }
            }
            
            // Filter out null reviews and map fields for compatibility
            const validReviews = visibleReviews
                .filter(review => review && review.id) // Filter out null/invalid reviews
                .map(review => ({
                    ...review,
                    // Ensure date field is available (backend uses date_of_recommendation, frontend expects created_at or date)
                    date: review.date_of_recommendation || review.created_at,
                }));
                
            // console.log('Processed reviews for display:', {
            //     provider_id: provider.id,
            //     total_visible_reviews: visibleReviews.length,
            //     valid_reviews_count: validReviews.length,
            //     reviewer_names: validReviews.map(r => r.user_name).filter(Boolean)
            // });
                
            setReviews(validReviews);
        } else {
            setReviews([]);
        }
        setLoadingReviews(false);
    }, [provider]);

    useEffect(() => {
        if (!id) {
            setLoadingStats(false);
            return;
        }
        const fetchReviewStats = async () => {
            setLoadingStats(true);
            try {
                const res = await fetch(`${API_URL}/api/reviews/stats/${id}`);
                if (!res.ok)
                    throw new Error(
                        `HTTP error! status: ${res.status} fetching review stats`
                    );
                const data = await res.json();
                setReviewStats({
                    average_rating: parseFloat(data.average_rating) || 0,
                    total_reviews: parseInt(data.total_reviews, 10) || 0,
                });
            } catch (errorCatch) {
                console.error("Failed to fetch review stats:", errorCatch);
                setError((prevError) =>
                    prevError
                        ? `${prevError}\n${errorCatch.message}`
                        : errorCatch.message
                );
                setReviewStats({ average_rating: 0, total_reviews: 0 });
            } finally {
                setLoadingStats(false);
            }
        };
        fetchReviewStats();
    }, [id]);

    const avgRating = (parseFloat(reviewStats.average_rating) || 0).toFixed(1);
    const totalReviews = parseInt(reviewStats.total_reviews, 10) || 0;

    const starCounts = useMemo(() => {
        const counts = [0, 0, 0, 0, 0];
        reviews.forEach((r) => {
            const rating = parseInt(r.rating);
            if (rating >= 1 && rating <= 5) {
                counts[rating - 1]++;
            }
        });
        return counts;
    }, [reviews]);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return "";
        }
    };

    const getWebsiteHostname = (url) => {
        if (!url) return "";
        try {
            const parsedUrl = new URL(
                url.startsWith("http") ? url : `https://${url}`
            );
            return parsedUrl.hostname.replace(/^www\./, "");
        } catch (e) {
            return url;
        }
    };

    const handleShareClick = () => {
        const shareUrl = `${window.location.origin}/provider/${provider?.id}`;
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
                setShowSharePopup(true);
                setTimeout(() => setShowSharePopup(false), 3000);
            })
            .catch((err) => {
                console.error("Failed to copy: ", err);
                // Show popup even if copy fails, with the URL
                setShowSharePopup(true);
                setTimeout(() => setShowSharePopup(false), 3000);
            });
    };

    const handleLikeClick = async () => {
        if (requireSignIn()) return;
        
        const providerId = provider.id;
        const originalLiked = isLiked;
        
        // Optimistic update
        setIsLiked(!isLiked);
        
        try {
            const response = await fetch(`${API_URL}/api/providers/${providerId}/like`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.id,
                    userEmail: user.primaryEmailAddress?.emailAddress
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ 
                    message: "Server error during like/unlike action." 
                }));
                throw new Error(errorData.message || `Failed to update like status. Status: ${response.status}`);
            }
            
            const result = await response.json();
            setIsLiked(result.currentUserLiked);
            
        } catch (error) {
            console.error("Error updating like status:", error.message);
            // Revert optimistic update on error
            setIsLiked(originalLiked);
            alert(`Failed to update like status: ${error.message}`);
        }
    };

    const handleBookmarkClick = () => {
        if (requireSignIn()) return;
        setIsBookmarked(!isBookmarked);
    };

    const handleQuoteClick = () => {
        if (requireSignIn()) return;
        setShowQuoteModal(true);
    };

    // Handle clicking on a user's name to navigate to their profile
    const handleNameClick = (displayName, username) => {
        // Use the actual username from the database if available, otherwise skip navigation
        if (username && username !== "Anonymous") {
            navigate(`/pro/${username}`);
        }
    };

    // Get unique trust network members (only visible reviewers)
    const trustNetworkMembers = useMemo(() => {
        const uniqueMembers = [];
        const seenNames = new Set();
        
        // Get from visible reviews - these are already properly filtered by backend visibility logic
        reviews.forEach((review) => {
            if (review.user_name && !seenNames.has(review.user_name)) {
                seenNames.add(review.user_name);
                uniqueMembers.push({
                    name: review.user_name,
                    username: review.user_username, // Include the actual username from database
                    initials: review.user_name.split(' ').map(n => n[0]).join('').toUpperCase(),
                });
            }
        });
        
        // Fallback: if no reviews but provider has users_who_reviewed (only for public endpoint)
        if (uniqueMembers.length === 0 && provider && provider.users_who_reviewed) {
            let usersWhoReviewed = [];
            try {
                // Handle both JSON string and array cases
                if (typeof provider.users_who_reviewed === 'string') {
                    usersWhoReviewed = JSON.parse(provider.users_who_reviewed);
                } else if (Array.isArray(provider.users_who_reviewed)) {
                    usersWhoReviewed = provider.users_who_reviewed;
                }
            } catch (e) {
                console.error('Error parsing users_who_reviewed:', e);
                usersWhoReviewed = [];
            }
            
            usersWhoReviewed.forEach((userName) => {
                if (userName && typeof userName === 'string' && !seenNames.has(userName)) {
                    seenNames.add(userName);
                    uniqueMembers.push({
                        name: userName,
                        username: null, // Username not available in fallback data
                        initials: userName.split(' ').map(n => n[0]).join('').toUpperCase(),
                    });
                }
            });
        }
        
        return uniqueMembers;
    }, [reviews, provider]);

    const recommendationRate = totalReviews > 0 ? Math.round((reviews.filter(r => r.rating >= 4).length / totalReviews) * 100) : 0;

    // Helper function to process images from provider data
    const getImageSrc = (photo) => {
        if (!photo) return null;
        if (typeof photo === 'string') return photo;
        if (photo.url) return photo.url;
        if (photo.gcs_path) {
            // For GCS paths, construct the direct GCS URL
            return `https://storage.googleapis.com/tried-and-trusted-images-production/${photo.gcs_path}`;
        }
        if (photo.data && photo.contentType) {
            const bufferData = photo.data.data;
            if (!bufferData) return null;
            try {
                const base64String = btoa(
                    new Uint8Array(bufferData).reduce(
                        (data, byte) => data + String.fromCharCode(byte),
                        ''
                    )
                );
                return `data:${photo.contentType};base64,${base64String}`;
            } catch (e) {
                console.error("Error converting image buffer for display:", e);
                return null;
            }
        }
        return null;
    };

    // Process provider images for display
    const providerImages = useMemo(() => {
        if (!provider?.images || !Array.isArray(provider.images)) {
            return [];
        }
        
        return provider.images
            .map(image => getImageSrc(image))
            .filter(src => src !== null);
    }, [provider]);

    if (loadingProvider || loadingStats) {
        return (
            <div id="provider-profile-page">
                <div className="page-container">
                    <div className="loading-state">Loading Profile...</div>
                </div>
            </div>
        );
    }

    if (error && !provider) {
        return (
            <div id="provider-profile-page">
                <div className="page-container">
                    <div className="error-state">
                        Error: {error}
                    </div>
                </div>
            </div>
        );
    }

    if (!provider) {
        return (
            <div id="provider-profile-page">
                <div className="page-container">
                    <div className="error-state">
                        Provider not found or not accessible.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="provider-profile-page">
            {showQuoteModal && (
                <QuoteModal
                    providerName={provider.business_name}
                    providerEmail={provider.email}
                    providerPhoneNumber={provider.phone_number}
                    onClose={() => setShowQuoteModal(false)}
                />
            )}
            {showSharePopup && (
                <div className="share-popup-overlay">
                    <div className="share-popup">
                        <h3>Successfully grabbed!</h3>
                        <p>Link copied to clipboard:</p>
                        <div className="share-url">
                            {`${window.location.origin}/provider/${provider?.id}`}
                        </div>
                        <button 
                            className="share-popup-close"
                            onClick={() => setShowSharePopup(false)}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
            <div className="page-container">
                {/* Hero Section */}
                <div className="hero-section">
                    <div className="hero-overlay" />
                    <div className="hero-content">
                        <div className="hero-header">
                            <h1 className="hero-title">{provider.business_name}</h1>
                            {provider.street_address && (
                                <p className="hero-address">{provider.street_address}</p>
                            )}
                            <div className="hero-meta">
                                <div className="hero-rating">
                                    <FaStar className="hero-rating-star" />
                                    <span className="hero-rating-score">{avgRating}</span>
                                    <span className="hero-rating-count">({totalReviews} reviews)</span>
                                </div>
                                {provider.category_name && (
                                    <span className="hero-badge">{provider.category_name}</span>
                                )}
                                {provider.service_scope === "local" && provider.city && provider.state && (
                                    <div className="hero-location">
                                        <FaMapMarkerAlt />
                                        <span>Local Favorite</span>
                                        {/* <span>•</span>
                                        <FaClock />
                                        <span>Open Now</span> */}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="hero-actions">
                            <button 
                                className={`hero-action-btn ${isLiked ? 'liked' : ''}`}
                                onClick={handleLikeClick}
                                title={!isSignedIn ? 'Sign in to like this provider' : (isLiked ? 'Unlike' : 'Like')}
                            >
                                {isLiked ? <FaHeart /> : <FaRegHeart />}
                                {isLiked ? 'Liked' : 'Like'}
                            </button>
                            <button 
                                className="hero-action-btn" 
                                onClick={handleShareClick}
                                title="Share this provider"
                            >
                                <FaShareAlt />
                                Share
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Layout */}
                <div className="main-layout">
                    {/* Main Content */}
                    <div className="main-content">
                        {/* Quick Actions */}
                        <div className="card">
                            <div className="card-content no-header">
                                <div className="quick-actions-grid">
                                    {provider.phone_number && (
                                        <a
                                            href={`tel:${provider.phone_number}`}
                                            className="action-button-large"
                                        >
                                            <FaPhone className="icon-md" />
                                            Call Now
                                        </a>
                                    )}
                                    {provider.phone_number && (
                                        <a
                                            href={`sms:${provider.phone_number}?body=Hi ${provider.business_name}, I saw a recommendation and wanted to connect.`}
                                            className="action-button-large outline"
                                        >
                                            <FaSms className="icon-md" />
                                            Send Message
                                        </a>
                                    )}
                                    <button 
                                        onClick={handleQuoteClick}
                                        className="action-button-large outline"
                                        title={!isSignedIn ? 'Sign in to request quotes' : 'Get Quote (AI Assistant)'}
                                    >
                                        <FaGem className="icon-md" />
                                        Get Quote (AI Assistant)
                                        {!isSignedIn && <span className="auth-required-indicator">*</span>}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Highlights */}
                        {Array.isArray(provider.tags) && provider.tags.length > 0 && (
                            <div className="card">
                                <div className="card-header">
                                    <h2 className="card-title">
                                        <FaAward className="icon-md" style={{ color: '#3b82f6' }} />
                                        What People Love
                                    </h2>
                                </div>
                                <div className="card-content">
                                    <div className="highlights-tags">
                                        {provider.tags.map((tag, i) => (
                                            <span key={i} className="highlight-tag">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                    {trustNetworkMembers.length > 0 && (
                                                                            <p className="also-recommended-text">
                                        Trusted By: {" "}
                                        {trustNetworkMembers.slice(0, 3).map((member, i) => (
                                            <span key={i}>
                                                <span 
                                                    className={`also-recommended-link ${member.username ? 'clickable-name' : ''}`}
                                                    onClick={() => member.username && handleNameClick(member.name, member.username)}
                                                    title={member.username ? `View ${member.name}'s profile` : member.name}
                                                >
                                                    {member.name}
                                                </span>
                                                {i < Math.min(2, trustNetworkMembers.length - 1) && ", "}
                                            </span>
                                        ))}
                                        {trustNetworkMembers.length > 3 && ` and ${trustNetworkMembers.length - 3} others`}
                                    </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="tabs-container">
                            <div className="tabs-list">
                                <button 
                                    className={`tab-trigger ${activeTab === 'reviews' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('reviews')}
                                >
                                    Reviews & Ratings
                                </button>
                                <button 
                                    className={`tab-trigger ${activeTab === 'details' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('details')}
                                >
                                    Business Details
                                </button>
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'reviews' && (
                            <div className="tab-content">
                                {/* Reviews List */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">User Reviews ({reviews.length})</h3>
                                    </div>
                                    <div className="card-content">
                                        {reviews.length === 0 ? (
                                            <p className="no-reviews-message">
                                                No reviews yet for this provider.
                                            </p>
                                        ) : (
                                            <div className="reviews-list">
                                                {reviews.map((review, i) => (
                                                    <div key={review.id || i} className="review-item">
                                                        <div className="review-header">
                                                            <div className="review-avatar">
                                                                {review.user_name ? 
                                                                    review.user_name.split(' ').map(n => n[0]).join('').toUpperCase() :
                                                                    'A'
                                                                }
                                                            </div>
                                                            <div className="review-content">
                                                                <div className="review-meta">
                                                                    <h4 
                                                                        className={`review-name ${review.user_username && review.user_name !== "Anonymous" ? "clickable-name" : ""}`}
                                                                        onClick={() => review.user_username && review.user_name !== "Anonymous" && handleNameClick(review.user_name, review.user_username)}
                                                                        title={review.user_username && review.user_name !== "Anonymous" ? `View ${review.user_name}'s profile` : undefined}
                                                                    >
                                                                        {review.user_name || "Anonymous"}
                                                                    </h4>
                                                                    <span className="review-date">
                                                                        {formatDate(review.date_of_recommendation || null)}
                                                                    </span>
                                                                </div>
                                                                <div className="review-stars">
                                                                    {[...Array(5)].map((_, j) => (
                                                                        <FaStar
                                                                            key={j}
                                                                            className={`review-star ${j < review.rating ? 'filled' : ''}`}
                                                                        />
                                                                    ))}
                                                                </div>
                                                                <p className="review-text">{review.content}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Rating Breakdown */}
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Rating Breakdown</h3>
                                    </div>
                                    <div className="card-content">
                                        <div className="rating-breakdown">
                                            {[5, 4, 3, 2, 1].map((star) => {
                                                const count = starCounts[star - 1];
                                                const percent = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                                                return (
                                                    <div key={star} className="rating-row">
                                                        <div className="rating-label">
                                                            <span>{star}</span>
                                                            <FaStar className="rating-label-star" />
                                                        </div>
                                                        <div className="rating-progress">
                                                            <div 
                                                                className="rating-progress-fill"
                                                                style={{ width: `${percent}%` }}
                                                            />
                                                        </div>
                                                        <span className="rating-count">{count}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'details' && (
                            <div className="tab-content">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Business Information</h3>
                                    </div>
                                    <div className="card-content">
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                            <div>
                                                <h4 style={{ fontWeight: 600, marginBottom: '1rem' }}>Contact</h4>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
                                                    {provider.website && (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <FaGlobe style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', color: '#6b7280' }} />
                                                            <a
                                                                href={provider.website.startsWith("http") ? provider.website : `https://${provider.website}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#2563eb', display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                                                            >
                                                                {getWebsiteHostname(provider.website)}
                                                                <FaExternalLinkAlt style={{ width: '0.75rem', height: '0.75rem', marginLeft: '0.25rem' }} />
                                                            </a>
                                                        </div>
                                                    )}
                                                    {provider.phone_number && (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <FaPhone style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', color: '#6b7280' }} />
                                                            <span>{provider.phone_number}</span>
                                                        </div>
                                                    )}
                                                    {provider.email && (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <FaEnvelope style={{ width: '1rem', height: '1rem', marginRight: '0.5rem', color: '#6b7280' }} />
                                                            <span>{provider.email}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* <div>
                                                <h4 style={{ fontWeight: 600, marginBottom: '1rem' }}>Specialties</h4>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                                                    <p>• {provider.service_type || 'Professional Service'}</p>
                                                    <p>• Quality Focused</p>
                                                    <p>• Trusted by Community</p>
                                                </div>
                                            </div> */}
                                        </div>
                                        {provider.description && (
                                            <div style={{ marginTop: '2rem' }}>
                                                <h4 style={{ fontWeight: 600, marginBottom: '1rem' }}>About</h4>
                                                <p style={{ color: '#374151', lineHeight: 1.6 }}>{provider.description}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="sidebar">
                        {/* Image Gallery */}
                        {providerImages.length > 0 && (
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Photos</h3>
                                </div>
                                <div className="card-content">
                                    <div className="provider-image-gallery">
                                        {providerImages.slice(0, 6).map((imageSrc, index) => (
                                            <div key={index} className="provider-image-item">
                                                <img 
                                                    src={imageSrc} 
                                                    alt={`${provider.business_name} - Photo ${index + 1}`}
                                                    className="provider-image"
                                                    loading="lazy"
                                                />
                                                {index === 5 && providerImages.length > 6 && (
                                                    <div className="provider-image-overlay">
                                                        <span className="provider-image-count">+{providerImages.length - 6}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Trust Network */}
                        {trustNetworkMembers.length > 0 && (
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Trust Network</h3>
                                </div>
                                <div className="card-content">
                                    <div className="trust-network-list">
                                        {trustNetworkMembers.slice(0, 5).map((member, i) => (
                                            <div key={i} className="trust-network-item">
                                                <div className="trust-network-avatar">
                                                    {member.initials}
                                                </div>
                                                <div className="trust-network-info">
                                                    <p 
                                                        className={`trust-network-name ${member.username ? 'clickable-name' : ''}`}
                                                        onClick={() => member.username && handleNameClick(member.name, member.username)}
                                                        title={member.username ? `View ${member.name}'s profile` : member.name}
                                                    >
                                                        {member.name}
                                                    </p>
                                                    <p className="trust-network-desc">Recommended this</p>
                                                </div>
                                            </div>
                                        ))}
                                        {trustNetworkMembers.length > 5 && (
                                            <div className="trust-network-item">
                                                <div className="trust-network-avatar">+</div>
                                                <div className="trust-network-info">
                                                    <p className="trust-network-name">{trustNetworkMembers.length - 5} more</p>
                                                    <p className="trust-network-desc">In your trust network</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CTA Card */}
                        <div className="card cta-card">
                            <div className="card-content no-header">
                                <h3 className="cta-title">Tried it too? Loved it?</h3>
                                <p className="cta-description">
                                    Share your experience and help your Trust Circle.
                                </p>
                                <button 
                                    className="cta-button"
                                    onClick={() => navigate('/share-recommendation')}
                                    title={!isSignedIn ? 'Sign in to share your own recommendations' : 'Share your recommendation'}
                                >
                                    Recommend it.
                                    {!isSignedIn && <span className="auth-required-indicator">*</span>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {!isSignedIn && (
                    <div className="public-user-note">
                        <p>* Sign in required for this action. You can browse all provider information without an account.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProviderProfile;
